import { useApolloClient } from '@apollo/client';
import {
  PageUrlDocument,
  type PageUrlQuery,
  type PageUrlQueryVariables,
  usePageUrlLazyQuery,
} from 'api/purchase-flow/generated';
import { AppApiVersions, LOGIN_PAGE_PATH } from 'app-constants';
import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const CACHE_KEY = 'page_url';

export type PageUrlNavigateOptions = {
  customCurrentPageUrl?: string;
};

export const useGetPageUrl = () => {
  const client = useApolloClient();
  const location = useLocation();
  const navigate = useNavigate();

  const [pageUrlAndNavigationProcessing, setPageUrlAndNavigationProcessing] =
    useState(false);

  const url = `${location.pathname}${location.search}`;
  const currentPageIsLogin = url.includes(`/${LOGIN_PAGE_PATH}`);

  const [requestPageUrl, { data, loading, error }] = usePageUrlLazyQuery({
    context: { apiVersion: AppApiVersions.purchaseFlow },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const getPageUrl = (variables: PageUrlQueryVariables) =>
    requestPageUrl({ variables });

  const getPageUrlAndNavigate = useCallback(
    (next: boolean | null = null, options?: PageUrlNavigateOptions) => {
      setPageUrlAndNavigationProcessing(true);
      const currentPageUrl = options?.customCurrentPageUrl || url;

      const isCustomCurrentPageUrlLogin =
        currentPageIsLogin && options?.customCurrentPageUrl?.match('/checkout');

      if (!isCustomCurrentPageUrlLogin) {
        client.cache.evict({
          fieldName: 'page_url',
          args: { url: CACHE_KEY, next: null },
        });
        client.cache.gc(); // Garbage collection
      }

      return getPageUrl({ url: currentPageUrl, next })
        .then(({ data: responseData }) => {
          // HANDLE EXTERNAL REDIRECT
          const externalUrl = responseData?.page_url?.external_url;
          if (externalUrl) {
            window.location.href = externalUrl;
            return new Promise<PageUrlQuery | undefined>(() => {});
          }

          // Write to cache to track the page url navigation result history
          client.writeQuery({
            query: PageUrlDocument,
            variables: {
              url: CACHE_KEY,
              next: null,
            },
            data: {
              page_url: responseData?.page_url,
            },
          });

          // HANDLE INTERNAL REDIRECT
          const nextPageUrl = responseData?.page_url?.url;
          if (nextPageUrl && nextPageUrl !== url) {
            navigate(nextPageUrl);
          }
          setTimeout(() => setPageUrlAndNavigationProcessing(false), 0);
          return responseData;
        })
        .catch((error) => {
          console.error('PageUrl navigation error:', error);
          setPageUrlAndNavigationProcessing(false);
          // Clear any potentially corrupted cache entries
          client.cache.evict({ fieldName: 'page_url' });
          client.cache.gc();
          throw error;
        });
    },
    [location],
  );

  return {
    getPageUrl,
    getPageUrlAndNavigate,
    pageUrl: data?.page_url?.url,
    pageUrlLoading: loading,
    pageUrlAndNavigationProcessing,
    pageUrlError: error,
  };
};

export const useCheckIsEqualCurrentPageUrl = () => {
  const client = useApolloClient();
  const { pathname, search } = useLocation();

  const cachedPageUrl = client.readQuery({
    query: PageUrlDocument,
    variables: { url: CACHE_KEY, next: null },
  });

  const currentPageUrl = `${pathname}${search}`;

  return cachedPageUrl?.page_url?.url === currentPageUrl;
};
